import { APP_ENVS } from '@/shared/constants/envs';

import { SOCIAL_MEDIA_NAMES } from '../constants/analytics-constants';
import {
	ChartDataPoint,
	WeeklyAvgTimeEntry,
	WeeklyCountEntry,
	WeeklyRateEntry,
} from '../types/analytics-types';

// Extended chart data point with date range for tooltips
export type ChartDataPointWithRange = ChartDataPoint & {
	labelRange?: string;
	weekStart?: string;
	weekEnd?: string;
	contextCount?: number;
	contextLabel?: string;
};

/**
 * Transform weekly count data (submissions, views) to chart format
 */
export function transformWeeklyCountToChartData(
	data?: WeeklyCountEntry[] | null
): ChartDataPointWithRange[] {
	if (!data?.length) return [];
	return data.map((entry) => ({
		label: formatWeekLabel(entry.weekStart),
		labelRange: formatWeekRange(entry.weekStart, entry.weekEnd),
		weekStart: entry.weekStart,
		weekEnd: entry.weekEnd,
		value: entry.count,
	}));
}

/**
 * Transform weekly rate data (review rate, response rate) to chart format
 * Includes submissionCount as context for tooltip display
 */
export function transformWeeklyRateToChartData(
	data?: WeeklyRateEntry[] | null
): ChartDataPointWithRange[] {
	if (!data?.length) return [];
	return data.map((entry) => ({
		label: formatWeekLabel(entry.weekStart),
		labelRange: formatWeekRange(entry.weekStart, entry.weekEnd),
		weekStart: entry.weekStart,
		weekEnd: entry.weekEnd,
		value: entry.rate,
		contextCount: entry.submissionCount,
		contextLabel: 'candidate',
	}));
}

/**
 * Transform weekly average time data (avg review time, avg response time) to chart format
 * Includes reviewedCount/respondedCount as context for tooltip display
 */
export function transformWeeklyAvgTimeToChartData(
	data?: WeeklyAvgTimeEntry[] | null,
	contextLabel: 'reviewed' | 'responded' = 'reviewed'
): ChartDataPointWithRange[] {
	if (!data?.length) return [];
	return data.map((entry) => ({
		label: formatWeekLabel(entry.weekStart),
		labelRange: formatWeekRange(entry.weekStart, entry.weekEnd),
		weekStart: entry.weekStart,
		weekEnd: entry.weekEnd,
		value: entry.avgTimeHours,
		contextCount: contextLabel === 'reviewed' ? entry.reviewedCount : entry.respondedCount,
		contextLabel,
	}));
}

export function formatWeekLabel(dateString?: string | null): string {
	if (!dateString) return '-';
	try {
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return '-';
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
		});
	} catch {
		return '-';
	}
}

export function formatWeekRange(weekStart?: string | null, weekEnd?: string | null): string {
	if (!weekStart || !weekEnd) return '-';
	try {
		const startDate = new Date(weekStart);
		const endDate = new Date(weekEnd);
		if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return '-';

		const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
		const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
		const startDay = startDate.getDate();
		const endDay = endDate.getDate();

		// If same month, show "May 9-15"
		if (startMonth === endMonth) {
			return `${startMonth} ${startDay}-${endDay}`;
		}
		// If different months, show "May 9-Jun 15"
		return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
	} catch {
		return '-';
	}
}

function formatOneDecimal(value: number): string {
	const s = value.toFixed(1);
	return s.endsWith('.0') ? s.slice(0, -2) : s;
}

export function formatPercentage(value?: number | null): string {
	if (value == null || isNaN(value)) return '-';
	return `${formatOneDecimal(value)}%`;
}

export function formatHours(hours?: number | null): string {
	if (hours == null || isNaN(hours) || hours === 0) return '-';
	if (hours < 1) {
		const minutes = Math.round(hours * 60);
		return `${minutes} mins`;
	}
	if (hours < 24) {
		return `${formatOneDecimal(hours)} hours`;
	}
	const days = hours / 24;
	if (days < 7) {
		return `${formatOneDecimal(days)} days`;
	}
	const weeks = days / 7;
	return `${formatOneDecimal(weeks)} weeks`;
}

export function formatNumber(value?: number | null): string {
	if (value == null || isNaN(value)) return '0';
	return value.toLocaleString();
}

export function getLatestValue(data?: ChartDataPoint[] | null): number {
	if (!data?.length) return 0;
	return data[data.length - 1]?.value ?? 0;
}

export function getAverageValue(data?: ChartDataPoint[] | null): number {
	if (!data?.length) return 0;
	const sum = data.reduce((acc, point) => acc + (point?.value ?? 0), 0);
	return sum / data.length;
}

export function formatViewDistribution(
	distribution?: Record<string, number> | null
): Array<{ source: string; percentage: number }> {
	if (!distribution) return [];

	const groupedSources: Record<string, number> = {};

	Object.entries(distribution).forEach(([source, percentage]) => {
		if (!source || percentage == null || percentage < 1) return;

		let displaySource = SOCIAL_MEDIA_NAMES[source] || source;

		if (displaySource.includes('accounts.google.com')) {
			displaySource = APP_ENVS.COMPANY_DOMAIN ?? 'direct';
		} else if (displaySource.includes('.')) {
			try {
				const url = new URL(
					displaySource.startsWith('http') ? displaySource : `https://${displaySource}`
				);
				const parts = url.hostname.split('.');
				displaySource = parts.length > 2 ? parts.slice(-2).join('.') : url.hostname;
			} catch {
				// Keep original if URL parsing fails
			}
		}

		groupedSources[displaySource] = (groupedSources[displaySource] || 0) + percentage;
	});

	return Object.entries(groupedSources)
		.sort(([, a], [, b]) => b - a)
		.slice(0, 3)
		.map(([source, percentage]) => ({
			source,
			percentage: Math.floor(percentage),
		}));
}

export function getUserTimezone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return 'UTC';
	}
}
